import { useCallback, useRef, useState } from 'react';
import { parseDrishtiResponse } from '../lib/parseDrishtiResponse';

/**
 * Chat state + network. Intentionally scoped to a single tab: no persistence,
 * no cross-session memory, no history UI (per brief §5 — deferred to v2.0).
 *
 * Exposes:
 *   messages          — array of { id, role, text, tag?, evidence?, errored? }
 *   send(text)        — sends user message, appends assistant reply
 *   isSending         — true while a request is in flight
 *   reset()           — clears the transcript (tab-scoped)
 *
 * Small client-side shortcut: "Hi"-style greetings get a canned personalised
 * reply without round-tripping to OpenAI. Instant response, zero cost. When
 * a real user profile lands, swap USER_NAME for the authenticated name.
 */

const MAX_MESSAGE_LEN = 2000;

// TODO: pull from the user profile once auth includes a name. Hardcoded
// for the single-user demo / meeting context.
const USER_NAME = 'Alok';

function isGreeting(text) {
  const cleaned = text
    .trim()
    .toLowerCase()
    .replace(/[.!?,]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned || cleaned.length > 40) return false;

  const single = new Set(['hi', 'hello', 'hey', 'yo', 'namaste', 'hola', 'howdy']);
  const phrases = [
    'good morning',
    'good afternoon',
    'good evening',
    'hi there',
    'hello there',
  ];

  // Allow any of: "hi", "hi drishti", "hello drishti", "good morning drishti", etc.
  const variants = [
    ...Array.from(single),
    ...Array.from(single).map((s) => `${s} drishti`),
    ...phrases,
    ...phrases.map((p) => `${p} drishti`),
  ];

  return variants.includes(cleaned);
}

function greetingReply() {
  return `Hi ${USER_NAME}, what would you like to know?`;
}

export default function useChat() {
  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const abortRef = useRef(null);

  const send = useCallback(async (rawText) => {
    const text = typeof rawText === 'string' ? rawText.trim() : '';
    if (!text || text.length > MAX_MESSAGE_LEN) return;
    if (abortRef.current) abortRef.current.abort();

    const userMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      text,
    };

    // Greeting shortcut — no network, no trust tag, no evidence. Just a
    // friendly reply so "Hi" doesn't waste an OpenAI round-trip.
    if (isGreeting(text)) {
      setMessages((prev) => [
        ...prev,
        userMessage,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          text: greetingReply(),
          tag: null,
          evidence: null,
        },
      ]);
      return;
    }

    const placeholder = {
      id: `a-${Date.now()}`,
      role: 'assistant',
      text: '',
      pending: true,
    };

    // Snapshot the transcript BEFORE appending, so the request payload
    // reflects the server's expected conversation-so-far.
    const priorConversation = messages
      .filter((m) => !m.pending && !m.errored)
      .map((m) => ({
        role: m.role,
        content: m.text,
      }));

    setMessages((prev) => [...prev, userMessage, placeholder]);
    setIsSending(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch('/api/drishti/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, conversation: priorConversation }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        replacePlaceholder(setMessages, placeholder.id, {
          role: 'assistant',
          text: body?.text || 'Something went wrong reaching Drishti. Try again.',
          tag: body?.tag || null,
          evidence: null,
          errored: true,
        });
        return;
      }

      const json = await res.json();
      const parsed = parseDrishtiResponse(json);

      replacePlaceholder(setMessages, placeholder.id, {
        role: 'assistant',
        text: parsed.text,
        tag: parsed.tag,
        evidence: parsed.evidence,
      });
    } catch (err) {
      if (err.name === 'AbortError') return;
      replacePlaceholder(setMessages, placeholder.id, {
        role: 'assistant',
        text: 'Network error reaching Drishti. Try again.',
        errored: true,
      });
    } finally {
      setIsSending(false);
      abortRef.current = null;
    }
  }, [messages]);

  const reset = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setMessages([]);
    setIsSending(false);
  }, []);

  return { messages, send, isSending, reset };
}

function replacePlaceholder(setMessages, id, next) {
  setMessages((prev) =>
    prev.map((m) => (m.id === id ? { ...m, ...next, id, pending: false } : m)),
  );
}
